import { Plus, X } from "lucide-react";
import { flatQuestions, optionsFor, CHOICE_TYPES } from "@/lib/forms";

const OPS = [["is", "is"], ["is_not", "is not"], ["contains", "contains"], ["gt", ">"], ["gte", "≥"], ["lt", "<"], ["lte", "≤"], ["answered", "is answered"], ["not_answered", "is empty"]];
const NO_VALUE = ["answered", "not_answered"];

function ValueInput({ q, c, set, i, j }) {
  if (NO_VALUE.includes(c.op)) return null;
  const opts = q && (CHOICE_TYPES.includes(q.type) || q.type === "yes_no") ? optionsFor(q) : null;
  const cls = "px-2 py-1 rounded border text-xs flex-1 min-w-[90px]";
  if (opts) return (
    <select className={cls} value={c.value ?? ""} onChange={(e) => set({ value: e.target.value })} data-testid={`logic-${i}-cond-${j}-value`}>
      <option value="">—</option>{opts.map((o, i) => <option key={`${i}-${o}`}>{o}</option>)}
    </select>
  );
  return <input className={cls} value={c.value ?? ""} onChange={(e) => set({ value: e.target.value })} data-testid={`logic-${i}-cond-${j}-value`} />;
}

export default function LogicEditor({ block, blocks, endings, variables = [], onChange }) {
  const lg = block.logic || { rules: [], otherwise: null };
  const qs = flatQuestions(blocks);
  const set = (p) => onChange({ logic: { ...lg, ...p } });
  const setRule = (i, p) => set({ rules: lg.rules.map((r, k) => (k === i ? { ...r, ...p } : r)) });
  const targets = [
    ...blocks.filter((b) => b.id !== block.id).map((b) => [b.id, `Go to · ${b.label || b.type}`]),
    ...endings.map((e) => [e.id, `End · ${e.title || e.id}${e.outcome !== "neutral" ? ` (${e.outcome})` : ""}`]),
  ];
  const refs = [
    ...blocks.flatMap((b) => (b.type === "group" ? (b.children || []).map((q) => [q.id, `${q.label || q.id} · in ${b.label || "group"}`]) : qs.some((q) => q.id === b.id) ? [[b.id, b.label || b.id]] : [])),
    ...variables.filter((v) => v.id).map((v) => [`var:${v.id}`, `= ${v.label || v.id} (calculated)`]),
    ["__score", "Total score"],
  ];
  const allRefs = (c) => (c.ref && !refs.some(([k]) => k === c.ref) ? [...refs, [c.ref, `${c.ref} (missing)`]] : refs);
  return (
    <div className="space-y-2 border-t pt-3" data-testid="logic-editor">
      <div className="text-[11px] text-neutral-500">Logic · after this step, the first matching rule decides where to go. Send leads to a "disqualified" ending to disqualify them.</div>
      {(lg.rules || []).map((r, i) => (
        <div key={i} className="panel-2 p-2 space-y-1.5" data-testid={`logic-rule-${i}`}>
          <div className="flex items-center gap-2 text-[11px] text-neutral-400">
            If
            <select className="px-1.5 py-0.5 rounded border text-[11px]" value={r.match || "all"} onChange={(e) => setRule(i, { match: e.target.value })} data-testid={`logic-${i}-match`}>
              <option value="all">all</option><option value="any">any</option>
            </select> of these match
            <button className="ml-auto btn-ghost p-0.5 hover:text-[var(--breach)]" onClick={() => set({ rules: lg.rules.filter((_, k) => k !== i) })} data-testid={`logic-${i}-del`}><X size={12} /></button>
          </div>
          {(r.conditions || []).map((c, j) => {
            const setC = (p) => setRule(i, { conditions: r.conditions.map((x, k) => (k === j ? { ...x, ...p } : x)) });
            return (
              <div key={j} className="flex flex-wrap gap-1.5 items-center">
                <select className="px-2 py-1 rounded border text-xs flex-1 min-w-[110px]" title={refs.find(([k]) => k === c.ref)?.[1]} value={c.ref} onChange={(e) => setC({ ref: e.target.value, value: "" })} data-testid={`logic-${i}-cond-${j}-ref`}>
                  {allRefs(c).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <select className="px-2 py-1 rounded border text-xs" value={c.op} onChange={(e) => setC({ op: e.target.value })} data-testid={`logic-${i}-cond-${j}-op`}>
                  {OPS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <ValueInput q={qs.find((q) => q.id === c.ref)} c={c} set={setC} i={i} j={j} />
                <button className="btn-ghost p-0.5" onClick={() => setRule(i, { conditions: r.conditions.filter((_, k) => k !== j) })}><X size={11} /></button>
              </div>
            );
          })}
          <button className="text-[11px] text-neutral-400 hover:text-white" onClick={() => setRule(i, { conditions: [...(r.conditions || []), { ref: block.type === "group" ? block.children?.[0]?.id : block.id, op: "is", value: "" }] })} data-testid={`logic-${i}-add-cond`}>+ condition</button>
          <select className="w-full px-2 py-1 rounded border text-xs" value={r.to || ""} onChange={(e) => setRule(i, { to: e.target.value })} data-testid={`logic-${i}-to`}>
            <option value="">Then…</option>{targets.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      ))}
      <button className="btn text-xs" data-testid="logic-add-rule"
        onClick={() => set({ rules: [...(lg.rules || []), { match: "all", conditions: [{ ref: block.type === "group" ? block.children?.[0]?.id || "__score" : SCREEN_REF(block), op: "is", value: "" }], to: "" }] })}>
        <Plus size={12} />Add rule
      </button>
      <label className="block text-[11px] text-neutral-500">Otherwise
        <select className="w-full mt-0.5 px-2 py-1 rounded border text-xs" value={lg.otherwise || ""} onChange={(e) => set({ otherwise: e.target.value || null })} data-testid="logic-otherwise">
          <option value="">Next step</option>{targets.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
    </div>
  );
}

const SCREEN_REF = (b) => (["welcome", "statement"].includes(b.type) ? "__score" : b.id);
